/**
 * Rent vs Buy Calculator Page
 * Compare the costs of renting versus buying a property
 */
import { useState, useEffect } from 'react';
import { useCustomTranslation } from '../../hooks';
import { trackPageView } from '../../utils/analytics';
import { Box, Typography, TextField, Button, Card, CardContent } from '@mui/material';
import AmazonRecommendation from '../../components/common/AmazonRecommendation';


interface RentVsBuyData {
  monthly_rent: number;
  property_price: number;
  down_payment: number;
  interest_rate: number;
  loan_term_years: number;
  property_tax_monthly: number;
  maintenance_monthly: number;
  insurance_monthly: number;
  years_to_compare: number;
}

interface RentVsBuyResults {
  total_rent_cost: number;
  total_buy_cost: number;
  monthly_mortgage: number;
  break_even_years: number;
  savings_or_cost: number;
}

export default function RentVsBuyPage() {
  const { t, i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';
  const [formData, setFormData] = useState<RentVsBuyData>({
    monthly_rent: 0,
    property_price: 0,
    down_payment: 0,
    interest_rate: 2.5,
    loan_term_years: 30,
    property_tax_monthly: 0,
    maintenance_monthly: 0,
    insurance_monthly: 0,
    years_to_compare: 5,
  });
  const [results, setResults] = useState<RentVsBuyResults | null>(null);

  useEffect(() => {
    trackPageView('/calculators/rent-vs-buy', 'Rent vs Buy Calculator');
    document.title = `${t('calculators.rentVsBuy.title')} | PayWise365`;
  }, [t]);

  const handleCalculate = () => {
    const loanAmount = formData.property_price - formData.down_payment;
    const monthlyRate = formData.interest_rate / 100 / 12;
    const totalMonths = formData.loan_term_years * 12;
    
    const monthly_mortgage = loanAmount * 
      (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / 
      (Math.pow(1 + monthlyRate, totalMonths) - 1);
    
    const total_rent_cost = formData.monthly_rent * 12 * formData.years_to_compare;
    const total_buy_cost = formData.down_payment + 
      (monthly_mortgage + formData.property_tax_monthly + formData.maintenance_monthly + formData.insurance_monthly) * 12 * formData.years_to_compare;
    
    const savings_or_cost = total_rent_cost - total_buy_cost;
    const break_even_years = formData.down_payment / (formData.monthly_rent - monthly_mortgage - formData.property_tax_monthly - formData.maintenance_monthly - formData.insurance_monthly) / 12;

    setResults({
      total_rent_cost,
      total_buy_cost,
      monthly_mortgage,
      break_even_years: break_even_years > 0 ? break_even_years : 0,
      savings_or_cost,
    });
  };

  return (
    <div className="calculator-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>{t('calculators.rentVsBuy.title')}</h1>
          <p>{t('calculators.rentVsBuy.pageSubtitle')}</p>
        </div>
      </div>
      
      <main className="calculator-content">
        <div className="calculator-container">
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {t('form.title')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <TextField
                    fullWidth
                    label="Monthly Rent (HKD)"
                    type="number"
                    value={formData.monthly_rent || ''}
                    onChange={(e) => setFormData({ ...formData, monthly_rent: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Property Price (HKD)"
                    type="number"
                    value={formData.property_price || ''}
                    onChange={(e) => setFormData({ ...formData, property_price: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Down Payment (HKD)"
                    type="number"
                    value={formData.down_payment || ''}
                    onChange={(e) => setFormData({ ...formData, down_payment: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Interest Rate (%)"
                    type="number"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({ ...formData, interest_rate: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Loan Term (Years)"
                    type="number"
                    value={formData.loan_term_years}
                    onChange={(e) => setFormData({ ...formData, loan_term_years: parseInt(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Property Tax (Monthly, HKD)"
                    type="number"
                    value={formData.property_tax_monthly || ''}
                    onChange={(e) => setFormData({ ...formData, property_tax_monthly: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Maintenance (Monthly, HKD)"
                    type="number"
                    value={formData.maintenance_monthly || ''}
                    onChange={(e) => setFormData({ ...formData, maintenance_monthly: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Insurance (Monthly, HKD)"
                    type="number"
                    value={formData.insurance_monthly || ''}
                    onChange={(e) => setFormData({ ...formData, insurance_monthly: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Years to Compare"
                    type="number"
                    value={formData.years_to_compare}
                    onChange={(e) => setFormData({ ...formData, years_to_compare: parseInt(e.target.value) || 5 })}
                  />
                </Box>
                <Box>
                  <Button variant="contained" fullWidth onClick={handleCalculate} size="large">
                    Calculate
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {results && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  Comparison Results
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Total Rent Cost ({formData.years_to_compare} years)</Typography>
                      <Typography variant="h5">HKD {results.total_rent_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Total Buy Cost ({formData.years_to_compare} years)</Typography>
                      <Typography variant="h5">HKD {results.total_buy_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'info.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Monthly Mortgage Payment</Typography>
                      <Typography variant="h5">HKD {results.monthly_mortgage.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: results.savings_or_cost > 0 ? 'success.main' : 'warning.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">{results.savings_or_cost > 0 ? 'Savings by Buying' : 'Extra Cost by Buying'}</Typography>
                      <Typography variant="h5">HKD {Math.abs(results.savings_or_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <div style={{ width: '100%', padding: '30px 20px' }}>
        <AmazonRecommendation isEnglish={isEnglish} />
      </div>
    </div>
  );
}
